// =============================================================================
// positioning-client.http.ts — Adaptateur HTTP RÉEL du PositioningClient (S17 / D2).
// =============================================================================
// Câble la SPA contre l'endpoint FOURNISSEUR de l'indicateur F-10 (contrat de route GELÉ D1).
// Substitué derrière le MÊME DI seam (PositioningClientProvider) : écrans/hooks consomment
// l'interface PositioningClient, l'impl est substituée sans toucher l'UI.
//
// SÉCURITÉ (RULE_REF_006) : le secret de session fournisseur (cookie httpOnly `supplier_session`)
// n'est JAMAIS lu ni fabriqué ici. La session est portée par le cookie (credentials: 'include').
// Le siret + le contact viennent de la session côté API (jamais du body / de l'URL). L'endpoint
// est un GET (le fournisseur LIT ses bornes) ⇒ AUCUN en-tête CSRF requis (le double-submit
// supplier_csrf ne couvre que les méthodes MUTANTES — parité operational-view / rate-collection).
//
// ANONYMAT (G0-S17-3) + UNAIRE (G0-S17-16) : l'API applique le masquage ET le verdict orange AVANT
// de répondre. Le front ne reçoit JAMAIS d'effectif (submission_count), ni de borne P25/P75, ni de
// TJM individuel : chaque couple revient en drapeau seul `{ masked, orange }`. Cet adaptateur ne
// fait que parser le JSON déjà filtré et déjà décidé par l'API.
//
// TIMEOUT BORNÉ : comme l'autosave (rate-collection-client.http.ts), on enveloppe la requête d'un
// AbortController armé — une coupure réseau ne doit pas laisser fetch PENDANT indéfiniment ; à
// l'expiration, fetch jette une AbortError (DOMException) que le hook TanStack traite en erreur.

import {
  PositioningApiError,
  type PositioningClient,
  type PositioningQuery,
  type PositioningResponseDto,
} from '@/api/positioning-client';

/** Forme attendue d'un corps d'erreur structuré (NestJS HttpException). */
interface StructuredErrorBody {
  readonly statusCode?: number;
  readonly message?: string;
}

/**
 * TIMEOUT CLIENT BORNÉ de la lecture F-10 (RÉSILIENCE, parité autosave). 3000 ms : réactif côté
 * fournisseur, sans avorter une réponse lente mais légitime. À l'abort, fetch jette une AbortError
 * (DOMException) propagée hors de request() ⇒ le hook TanStack la remonte en erreur de query.
 */
const POSITIONING_REQUEST_TIMEOUT_MS = 3000;

/**
 * Adaptateur HTTP réel : `fetch` contre l'API fournisseur F-10. `baseUrl` par défaut vide (chemins
 * relatifs : SPA et API partagent l'origine, ou un reverse proxy route `/supplier/*`). En test, on
 * injecte un baseUrl ou on mocke `fetch`.
 */
export class HttpPositioningClient implements PositioningClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  public async getPositioning(
    campaignId: string,
    query: PositioningQuery,
  ): Promise<PositioningResponseDto> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, POSITIONING_REQUEST_TIMEOUT_MS);
    try {
      // Route GELÉE D1 : ressource = campaignId d'URL ; couple + tjm SAUVEGARDÉ = query string.
      const search = new URLSearchParams();
      search.set('cycleNumber', String(query.cycleNumber));
      if (query.profileCode !== undefined) {
        search.set('profileCode', query.profileCode);
      }
      if (query.seniorityLevel !== undefined) {
        search.set('seniorityLevel', query.seniorityLevel);
      }
      if (query.tjmValueEur !== undefined) {
        search.set('tjmValueEur', String(query.tjmValueEur));
      }
      const response = await fetch(
        `${this.baseUrl}/supplier/positioning/${encodeURIComponent(
          campaignId,
        )}?${search.toString()}`,
        {
          method: 'GET',
          // Session fournisseur = cookie httpOnly `supplier_session` : transmis avec credentials.
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          signal: controller.signal,
        },
      );
      if (!response.ok) {
        throw await this.toApiError(response);
      }
      return (await response.json()) as PositioningResponseDto;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /** Convertit une réponse non-OK en erreur applicative typée (message serveur tel quel). */
  private async toApiError(response: Response): Promise<PositioningApiError> {
    let body: StructuredErrorBody | undefined;
    try {
      body = (await response.json()) as StructuredErrorBody;
    } catch {
      body = undefined;
    }
    const message =
      body?.message ??
      `Erreur API (${String(response.status)}) sur l'indicateur de positionnement.`;
    return new PositioningApiError(message);
  }
}
